import React, { useState } from 'react';
import {
    Typography,
    Box,
    Button,
} from '@mui/material';
import {
    CloudUpload as CloudUploadIcon,
    Download as DownloadIcon
} from '@mui/icons-material';

import {
    validateCsvFile,
    createFileInput,
    downloadTemplate,
    formatBulkUploadMessage
} from './logic';
import {
    StyledDropzone,
    csvButtonRowStyles,
    cancelButtonStyles,
    submitButtonStyles,
    downloadButtonStyles,
    csvUploadSectionStyles,
    csvDownloadCenterStyles
} from './styles';

interface CsvUploadFormProps {
    onUpload: (file: File) => void;
    onCancel: () => void;
    isLoading: boolean;
    onError: (message: string) => void;
}

const CsvUploadForm: React.FC<CsvUploadFormProps> = ({
    onUpload,
    onCancel,
    isLoading,
    onError
}) => {
    const [selectedFile, setSelectedFile] = useState<File | null>(null);

    const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file && validateCsvFile(file)) {
            setSelectedFile(file);
        } else {
            onError('Please select a valid CSV file');
            event.target.value = '';
        }
    };

    const handleBulkUpload = () => {
        if (!selectedFile) {
            onError('Please select a CSV file first');
            return;
        }
        onUpload(selectedFile);
    };

    const handleDropzoneClick = () => {
        const input = createFileInput(handleFileSelect);
        input.click();
    };

    const handleCancel = () => {
        setSelectedFile(null);
        onCancel();
    };

    return (
        <Box sx={csvUploadSectionStyles}>
            <Box sx={csvDownloadCenterStyles}>
                <Button
                    variant="outlined"
                    startIcon={<DownloadIcon />}
                    onClick={downloadTemplate}
                    sx={downloadButtonStyles}
                >
                    Download CSV Template
                </Button>
            </Box>

            <StyledDropzone onClick={handleDropzoneClick}>
                <CloudUploadIcon sx={{ fontSize: 48, color: '#9CA3AF', mb: 2 }} />
                {selectedFile ? (
                    <>
                        <Typography variant="h6" sx={{ mb: 1, color: '#4566EB' }}>
                            File selected: {selectedFile.name}
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#6B7280', mb: 2 }}>
                            Size: {(selectedFile.size / 1024).toFixed(2)} KB
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#9CA3AF' }}>
                            Click to select a different file
                        </Typography>
                    </>
                ) : (
                    <>
                        <Typography variant="h6" sx={{ mb: 1, color: '#374151' }}>
                            Drop your CSV file here
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#6B7280', mb: 2 }}>
                            or click to browse
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#9CA3AF' }}>
                            Only .csv files supported (up to 500 rows)
                        </Typography>
                    </>
                )}
            </StyledDropzone>

            {/* CSV Upload Buttons */}
            <Box sx={csvButtonRowStyles}>
                <Button
                    onClick={handleCancel}
                    disabled={isLoading}
                    sx={cancelButtonStyles}
                >
                    Cancel
                </Button>
                <Button
                    onClick={handleBulkUpload}
                    disabled={!selectedFile || isLoading}
                    sx={submitButtonStyles}
                >
                    {isLoading ? 'Uploading...' : 'Upload'}
                </Button>
            </Box>
        </Box>
    );
};

export default CsvUploadForm;
